import { useEffect, useState } from 'react'
import { fontFaceCss, type FontData } from '@orisini/studio-kit/fonts'
import { RATIO_SIZE, type Ratio } from '@orisini/studio-kit/ratios'
import { buildDocument, validateValues } from '@orisini/studio-kit/templates/document'
import { sampleValues, toTemplateDef, type DataTemplate } from '@orisini/studio-kit/templates/data-template'
import { loadFonts } from '../../content/lib/fonts'

export interface TemplateDoc {
  key: string
  label: string
  ratio: Ratio
  doc: string
}

/**
 * Dokumen preview template data dengan nilai contohnya — dibangun di browser
 * dengan studio-kit yang sama dengan render server, jadi gratis dan identik.
 *
 * Poster: satu dokumen per rasio. Carousel: satu dokumen per slide contoh,
 * pada satu rasio.
 */
export function useTemplateDocs(def: DataTemplate | null, ratios: readonly Ratio[]): { docs: TemplateDoc[]; error: string | null } {
  const [fonts, setFonts] = useState<FontData | null>(null)
  const [state, setState] = useState<{ docs: TemplateDoc[]; error: string | null }>({ docs: [], error: null })

  useEffect(() => {
    let batal = false
    loadFonts()
      .then((f) => !batal && setFonts(f))
      .catch(() => !batal && setState({ docs: [], error: "Couldn't load fonts for the preview" }))
    return () => {
      batal = true
    }
  }, [])

  const kunci = JSON.stringify([def, ratios])
  useEffect(() => {
    if (!def || !fonts) return
    try {
      const css = fontFaceCss(def.fonts, fonts)
      const ref = { code: 'preview', version: 0 }
      const docs: TemplateDoc[] = []
      if (def.kind === 'poster') {
        const t = toTemplateDef(def, ref)
        const v = validateValues(t.fields, sampleValues(def.fields), { draft: true })
        for (const r of ratios) docs.push({ key: r, label: r, ratio: r, doc: buildDocument(t, v, RATIO_SIZE[r], css) })
      } else {
        const slides = def.sampleSlides ?? []
        const r = ratios[0] ?? '4:5'
        slides.forEach((s, i) => {
          const video = def.kind === 'video'
          const t = toTemplateDef(def, ref, { index: i + 1, total: slides.length, narration: video ? 'A sample line of what the voice says in this scene.' : undefined })
          const v = validateValues(t.fields, { ...sampleValues(def.fields), ...s }, { draft: true })
          const rr = video ? '9:16' : r
          docs.push({ key: `${i}`, label: `${video ? 'Scene' : 'Slide'} ${i + 1}`, ratio: rr, doc: buildDocument(t, v, RATIO_SIZE[rr], css) })
        })
      }
      setState({ docs, error: null })
    } catch (err) {
      setState({ docs: [], error: err instanceof Error ? err.message : "Couldn't build the preview" })
    }
    // `kunci` mewakili def + ratios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kunci, fonts])

  return state
}
